import { FormControlFeedbackComponent, DatepickerComponent, PopoverComponent, PortalRequestStatusButtonComponent } from '@aston/foundation/components';
import { UntypedFormBuilder, UntypedFormControl, Validators, FormsModule, ReactiveFormsModule, ValidatorFn } from '@angular/forms';
import { CustomValidators, UnsubscriberClass } from '@aston/foundation/classes';
import { ActionType, DunningLevel } from '@aston/foundation/enums';
import { addDays, toDateOnly } from '@aston/foundation/functions';
import { Component, inject, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { filter, pairwise, tap } from 'rxjs';

import { mapToCreateDebtorPortalRequest } from '../../functions/mappers.request';
import { CreateDebtorPortalRequest } from '../../models/request-create.model';
import { PortalRequest } from '../../models/request.model';

// User Story 39497: [Portail][Demande] - Permettre au client de faire une demande pour une ou plusieurs pièces comptables

@Component({
	selector: 'open-request-modal',
	templateUrl: './open-request-modal.component.html',
	imports: [FormsModule, ReactiveFormsModule, TranslateModule, FormControlFeedbackComponent, DatepickerComponent, PopoverComponent, PortalRequestStatusButtonComponent]
})
export class OpenRequestModalComponent extends UnsubscriberClass {

	private activeModal = inject(NgbActiveModal)
	private formBuilder = inject(UntypedFormBuilder)

	DunningLevel = DunningLevel
	tmrow = addDays(new Date(), 1)

	readonly = signal(false)
	answer = output<Partial<CreateDebtorPortalRequest>>()

	type = () => this.form?.get('requestType')?.value
	needDate = () => [DunningLevel.PAA].includes(this.type())
	needComment = () => [DunningLevel.LTG, DunningLevel.PPA].includes(this.type())

	form = this.formBuilder.group({
		portalRequestId: new UntypedFormControl(''),
		portalRequestFunctionalId: new UntypedFormControl(''),
		portalRequestStatus: new UntypedFormControl(''),
		requestType: new UntypedFormControl('', [Validators.required]),
		comment: new UntypedFormControl('', [this.requiredForTypes(DunningLevel.PPA, DunningLevel.LTG), Validators.maxLength(2000)]),
		email: new UntypedFormControl('', [Validators.required]),
		paymentDate: new UntypedFormControl('', [this.requiredForTypes(DunningLevel.PAA), CustomValidators.afterNow(true)]),
	})

	resetOnTypeChange$ = this.form.get('requestType').valueChanges
		.pipe(
			pairwise(),
			filter(([prev, current]) => prev && prev !== current),
			tap(_ => this.form.reset()),
			takeUntilDestroyed(),
		)
		.subscribe()

	requiredForTypes(...types: (string|DunningLevel)[]): ValidatorFn {
		return (control: UntypedFormControl) => {
			if (types.includes(this.type())) return Validators.required(control)
			return null;
		}
	}

	loadFromRequest(request: PortalRequest) {
		this.form.patchValue({
			portalRequestId: request.portalRequestId,
			portalRequestFunctionalId: request.portalRequestFunctionalId,
			portalRequestStatus: request.portalRequestStatus,
			requestType: request.dunningLevel,
			comment: request.requestCommentary,
			email: request.answerEmail,
			paymentDate: request.paymentDate ? new Date(request.paymentDate) : null,
		})
		this.form.disable()
	}

	submit() {
		this.answer.emit(mapToCreateDebtorPortalRequest({
			email: this.form.get('email').value,
			content: this.form.get('comment').value,
			paymentDate: this.form.get('paymentDate').value ? toDateOnly(this.form.get('paymentDate').value) : undefined,
			dunningLevel: this.form.get('requestType').value,
		}));
	}

	dismiss() {
		this.answer.emit(null)
		this.activeModal.dismiss(ActionType.CANCEL)
	}
}
